import { useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const PRESETS = [
  { key: "today", label: "Hoy", icon: "📅" },
  { key: "week", label: "Esta semana", icon: "📆" },
  { key: "month", label: "Este mes", icon: "🗓" },
  { key: "year", label: "Este año", icon: "📊" },
  { key: "all", label: "Todo", icon: "♾️" },
  { key: "custom", label: "Personalizado", icon: "📌" },
];

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/**
 * Compute the [start, end] Date range for a given preset + anchor date + custom values.
 * end is INCLUSIVE (represents the last full day).
 */
export function computeRange({ preset, anchor, customFrom, customTo }) {
  const now = anchor ? new Date(anchor) : new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  if (preset === "all") return { start: null, end: null, label: "Todos los movimientos" };

  if (preset === "today") {
    return { start: startOfDay(now), end: endOfDay(now), label: now.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) };
  }

  if (preset === "week") {
    const dow = now.getDay(); // 0=Sun
    const monday = new Date(now); monday.setDate(now.getDate() - ((dow + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return {
      start: startOfDay(monday),
      end: endOfDay(sunday),
      label: `${monday.toLocaleDateString("es-MX", { day: "numeric", month: "short" })} – ${sunday.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}`,
    };
  }

  if (preset === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: startOfDay(first),
      end: endOfDay(last),
      label: `${MONTHS_ES[now.getMonth()]} ${now.getFullYear()}`,
    };
  }

  if (preset === "year") {
    const first = new Date(now.getFullYear(), 0, 1);
    const last = new Date(now.getFullYear(), 11, 31);
    return { start: startOfDay(first), end: endOfDay(last), label: `${now.getFullYear()}` };
  }

  if (preset === "custom") {
    const from = parseISO(customFrom);
    const to = parseISO(customTo);
    if (from && to) return { start: startOfDay(from), end: endOfDay(to), label: `${customFrom} → ${customTo}` };
    if (from) return { start: startOfDay(from), end: null, label: `Desde ${customFrom}` };
    if (to) return { start: null, end: endOfDay(to), label: `Hasta ${customTo}` };
    return { start: null, end: null, label: "Rango personalizado" };
  }

  return { start: null, end: null, label: "" };
}

/**
 * DateFilterBar — preset chips + smart navigation.
 * Props:
 *  - value: { preset, anchor, customFrom, customTo }
 *  - onChange(value): void
 *  - testid?: string (prefix for data-testids, default "date-filter")
 */
export default function DateFilterBar({ value, onChange, testid = "date-filter" }) {
  const { preset = "month", anchor = new Date().toISOString(), customFrom = "", customTo = "" } = value || {};

  const range = useMemo(() => computeRange({ preset, anchor, customFrom, customTo }), [preset, anchor, customFrom, customTo]);

  const set = (patch) => onChange({ preset, anchor, customFrom, customTo, ...patch });

  const shiftAnchor = (delta) => {
    const cur = new Date(anchor || new Date());
    if (preset === "week") cur.setDate(cur.getDate() + delta * 7);
    else if (preset === "month") cur.setMonth(cur.getMonth() + delta);
    else if (preset === "year") cur.setFullYear(cur.getFullYear() + delta);
    else if (preset === "today") cur.setDate(cur.getDate() + delta);
    set({ anchor: cur.toISOString() });
  };

  const canNavigate = ["today", "week", "month", "year"].includes(preset);
  const anchorDate = new Date(anchor || new Date());

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear + 2; y >= currentYear - 10; y--) years.push(y);

  const jumpToMonth = (monthIdx) => {
    const d = new Date(anchorDate); d.setMonth(monthIdx);
    set({ preset: "month", anchor: d.toISOString() });
  };
  const jumpToYear = (year) => {
    const d = new Date(anchorDate); d.setFullYear(year);
    set({ anchor: d.toISOString() });
  };

  return (
    <div className="date-filter-bar" data-testid={testid}>
      <div className="dfb-chips" data-testid={`${testid}-chips`}>
        {PRESETS.map((p) => (
          <button
            type="button"
            key={p.key}
            className={preset === p.key ? "dfb-chip active" : "dfb-chip"}
            onClick={() => set({ preset: p.key, anchor: new Date().toISOString() })}
            data-testid={`${testid}-preset-${p.key}`}
          >
            <span aria-hidden>{p.icon}</span> {p.label}
          </button>
        ))}
      </div>

      <div className="dfb-controls">
        <div className="dfb-nav">
          <button
            type="button"
            className="dfb-nav-btn"
            disabled={!canNavigate}
            onClick={() => shiftAnchor(-1)}
            data-testid={`${testid}-prev`}
            aria-label="Anterior"
          ><ChevronLeft size={16} /></button>

          <div className="dfb-label" data-testid={`${testid}-label`}>
            <CalendarIcon size={14} />
            <span>{range.label || "Sin filtro"}</span>
          </div>

          <button
            type="button"
            className="dfb-nav-btn"
            disabled={!canNavigate}
            onClick={() => shiftAnchor(1)}
            data-testid={`${testid}-next`}
            aria-label="Siguiente"
          ><ChevronRight size={16} /></button>
        </div>

        {preset === "month" && (
          <div className="dfb-selects">
            <select
              value={anchorDate.getMonth()}
              onChange={(e) => jumpToMonth(Number(e.target.value))}
              data-testid={`${testid}-month-select`}
              aria-label="Mes"
            >
              {MONTHS_ES.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <select
              value={anchorDate.getFullYear()}
              onChange={(e) => jumpToYear(Number(e.target.value))}
              data-testid={`${testid}-year-select`}
              aria-label="Año"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}

        {preset === "year" && (
          <div className="dfb-selects">
            <select
              value={anchorDate.getFullYear()}
              onChange={(e) => jumpToYear(Number(e.target.value))}
              data-testid={`${testid}-year-select`}
              aria-label="Año"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}

        {preset === "custom" && (
          <div className="dfb-custom">
            <label>
              Desde
              <input
                type="date"
                value={customFrom || ""}
                onChange={(e) => set({ customFrom: e.target.value })}
                data-testid={`${testid}-custom-from`}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={customTo || ""}
                onChange={(e) => set({ customTo: e.target.value })}
                data-testid={`${testid}-custom-to`}
              />
            </label>
            {(customFrom || customTo) && (
              <button
                type="button"
                className="dfb-clear"
                onClick={() => set({ customFrom: "", customTo: "" })}
                data-testid={`${testid}-custom-clear`}
                aria-label="Limpiar rango"
              ><X size={14} /></button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Default initial value for a DateFilterBar consumer. */
export function makeInitialDateFilter() {
  return { preset: "month", anchor: new Date().toISOString(), customFrom: "", customTo: "" };
}

/** Test if an ISO date string falls within the currently-selected range. */
export function isDateInRange(iso, range) {
  if (!iso) return false;
  if (!range.start && !range.end) return true; // "all"
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  if (range.start && t < range.start.getTime()) return false;
  if (range.end && t > range.end.getTime()) return false;
  return true;
}
